import React from 'react'
import { useHistory } from 'react-router-dom'
import { useAnalytics } from 'reactfire'

import {
  ListItem,
  InnerLinkWrap,
  ProfilePicture,
  SpacingWrap,
  ButtonsWrap,
  Button,
  ButtonLink,
  ImageWrap,
} from './styled'
import { Highlighted } from './Highlighted'
import { getNotificationLink } from './utils'

import { INotification, NotificationType } from 'src/api/notifications/types'
import {
  denyConnection,
  approveConnection,
} from 'src/api/connections/connections'
import { analyticsEvents } from 'src/constants/analytics/events'
import {
  LEADER_BOARD_DETAIL,
  NEW_QUIZ,
  COMPLETE_QUIZ,
} from 'src/constants/routes'
import { getQuiz } from 'src/api/quizzes/quizzes'
import { LoadingIcon } from 'src/components/Icons'

interface NotificationLinkBodyProps {
  notification: INotification
  refetchNotifications?: () => void
}
const NotificationLinkBody: React.FC<NotificationLinkBodyProps> = ({
  notification,
  refetchNotifications,
}) => {
  const { logEvent } = useAnalytics()
  const [isConfirming, setIsConfirming] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)

  const handleConfirmFriendRequest = async () => {
    setIsConfirming(true)
    try {
      await approveConnection(notification.otherUserId)
      logEvent(analyticsEvents.notificationFriendshipApproved)
      refetchNotifications && refetchNotifications()
    } catch (error) {
      console.error('unhandled error', error)
    }
    setIsConfirming(false)
  }

  const handleDeleteFriendRequest = async () => {
    setIsDeleting(true)
    try {
      await denyConnection(notification.otherUserId)
      logEvent(analyticsEvents.notificationFriendshipRejected)
      refetchNotifications && refetchNotifications()
    } catch (error) {
      console.error('unhandled error', error)
    }
    setIsDeleting(false)
  }

  return (
    <InnerLinkWrap>
      <ImageWrap>
        <ProfilePicture src={notification.imageUrl} />
      </ImageWrap>
      <SpacingWrap>
        <Highlighted
          text={notification.description}
          highlightedWords={notification.highlightedWords}
        />
        {notification.eventType === NotificationType.FriendRequestNew && (
          <ButtonsWrap>
            <Button
              onClick={handleConfirmFriendRequest}
              disabled={isConfirming || isDeleting}
            >
              {isConfirming ? <LoadingIcon size="16" /> : 'Confirm'}
            </Button>
            <Button
              secondary
              onClick={handleDeleteFriendRequest}
              disabled={isConfirming || isDeleting}
            >
              {isDeleting ? <LoadingIcon size="16" /> : 'Delete'}
            </Button>
          </ButtonsWrap>
        )}
      </SpacingWrap>
    </InnerLinkWrap>
  )
}

interface Props {
  notification: INotification
  refetchNotifications: () => void
}
export const NotificationItem: React.FC<Props> = ({
  notification,
  refetchNotifications,
}) => {
  const { push } = useHistory()
  const { logEvent } = useAnalytics()

  const notificationLink = React.useMemo(
    () => getNotificationLink(notification),
    [notification]
  )

  const handleNotificationClick = async () => {
    if (notificationLink.includes(LEADER_BOARD_DETAIL.slice(0, -3))) {
      logEvent(analyticsEvents.notificationProfileOpened)
    }
    if (notificationLink.includes(NEW_QUIZ.slice(0, -3))) {
      /**
       * Prevent sending 3 analytics events (Notification_Quiz_Started, Quiz_Started, Quiz_Results_Displayed)
       *   if user has already completed the quiz, send only Quiz_Results_Displayed
       */
      const quizDetail = await getQuiz(String(notification.quizId))
      if (quizDetail.completed) {
        return push(COMPLETE_QUIZ.replace(':id', String(notification.quizId)))
      }
      logEvent(analyticsEvents.notificationQuizStarted)
    }
    if (notificationLink.includes(COMPLETE_QUIZ.slice(0, -3))) {
      logEvent(analyticsEvents.notificationQuizResults)
    }
    push(notificationLink)
  }

  return (
    <ListItem>
      {notification.eventType === NotificationType.FriendRequestNew ||
      // TODO: reconsider this condition, check if connectionId is always present
      (notification.eventType === NotificationType.FriendRequestApproved &&
        notification.connectionId === undefined) ? (
        <NotificationLinkBody
          notification={notification}
          refetchNotifications={refetchNotifications}
        />
      ) : (
        <ButtonLink onClick={handleNotificationClick}>
          <NotificationLinkBody notification={notification} />
        </ButtonLink>
      )}
    </ListItem>
  )
}
